/* eslint-disable no-undef */

import Page from '/components/templates/Page'
import Welcome from '/components/pages/single-property/Welcome'
import Introduction from '/components/pages/single-property/Introduction'
import Gallery from '/components/pages/single-property/Gallery'
import Rentals from '/components/pages/common/Rentals'
import Details from '/components/pages/single-property/Details'
import Availability from '/components/pages/single-property/Availability'
import Contact from '/components/pages/common/Contact'
import axios from '/components/configs/axios'
import styles from './styles.module.scss'
import Card from '/components/pages/single-property/components/Card'
import Amenities from '/components/pages/singleEstate/Amenities'
import { withModifiers } from '/components/helpers/withModifiers'
import Map from '/components/pages/single-property/Map'
import { useResponsive } from '../../providers/ResponsiveContext'
import FourOhFour from '../404'
import { useRouter } from 'next/router'
import { getS3Url } from '../../components/helpers/getS3Url'
import LoadingPage from '../../components/templates/LoadingPage'
import isEmpty from 'lodash-es/isEmpty'
import footerTranslation from '/components/general/Footer/footerTranslations'
import headerTranslation from '/components/general/Header/headerTranslation'
import contactTranslation from '/components/pages/common/Contact/contactTranslations'
import cache from 'memory-cache'
import singlePropertyTranslations from '/components/pages/single-property/singlePropertyTranslation'
import generalEstateTranslations from '/components/pages/real-estate/generalEstateTranslations'

const RealEstateProperty = ({
  property,
  headerTranslation,
  footerTranslation,
  contactTranslation,
  pageTranslations,
  generalTranslation,
  notFound,
  recommendedProperties
}) => {
  const { isTablet } = useResponsive()
  const router = useRouter()

  const loadingTitle = {
    en: 'Loading',
    fr: 'Chargement',
    es: 'Cargando',
    nl: 'Bezig met laden'
  }

  if (router.isFallback) {
    return <LoadingPage title={loadingTitle[router.locale]} />
  }

  if (notFound) {
    return (
      <FourOhFour
        headerTranslation={headerTranslation}
        footerTranslation={footerTranslation}
      />
    )
  }

  const more_information = property?.more_information?.map((info) => {
    return {
      value: info.title,
      title: info.content
    }
  })

  if (property) {
    return (
      <Page
        headerTranslation={headerTranslation}
        footerTranslation={footerTranslation}
        variant={['white']}
        meta={{
          title: `${property?.title} | Real Estate - St. Martin Sotheby's Realty`,
          description: property.short_description,
          image: property.cloudflare_intro_image
            ? getS3Url(property.cloudflare_intro_image)
            : property.cloudflare_gallery?.[0]
            ? getS3Url(property.cloudflare_gallery[0])
            : null
        }}
        footer={false}
      >
        <Welcome
          cardTranslations={generalTranslation}
          translations={pageTranslations}
          image={property.intro_image}
          title={property.title}
          bedrooms={property.bedroom_no}
          type={property.property_type}
          property={property}
          description={property.short_description}
          background={
            getS3Url(property?.cloudflare_intro_image, '1980') ||
            getS3Url(property.cloudflare_gallery?.[0])
          }
          video={property?.intro_video}
          sold={property?.sold}
          cloudflare={property?.cloudflare_intro_video}
        />

        <Introduction
          location={`${property?.locations[0]}, ${property.address}, ${property.county}`}
          title={`${pageTranslations.introduction.about[router.locale]} ${
            property.title
          }`}
          description={property.long_description}
          contact={property.intro_contact_person?.[0]}
          translations={pageTranslations.introduction}
        />

        <Gallery
          key={property.id}
          property={property}
          translations={pageTranslations.gallery}
        />

        <div className={styles.grid}>
          {!isTablet && (
            <div className={withModifiers('grid__item', ['fixed'], styles)}>
              <div className={styles.grid__inner}>
                <Card
                  cardTranslations={generalTranslation}
                  translations={pageTranslations.welcome.card}
                  accommodates={false}
                  bedrooms={property.bedroom_no}
                  bathrooms={property.bathroom_no}
                  property={property}
                />
              </div>
            </div>
          )}

          {more_information && !isEmpty(more_information) && (
            <div className={withModifiers('grid__item', ['first'], styles)}>
              <Details
                translations={pageTranslations.details}
                title="More Information"
                more_information={more_information}
              />
            </div>
          )}

          <div className={styles.grid__item}>
            <Amenities
              translations={pageTranslations.amenities}
              property={property}
            />
          </div>

          <div className={styles.grid__item}>
            <Availability
              translations={pageTranslations}
              title={`${
                pageTranslations.amenities.estateTitle[router.locale]
              } ${property?.locations[0]}`}
              description={property.locations[1]}
              variant={[property.lng && property.lat && 'pb0']}
            />
          </div>
        </div>

        {property.lat && property.lng && (
          <Map
            variant={['section', 'pt0']}
            lng={property.lng}
            lat={property.lat}
          />
        )}

        <Contact
          translations={contactTranslation}
          route="real-estate/contact-email"
          leftLabel={false}
          buying
          staff={property?.contact_person}
          propertyName={property?.title}
          propertyId={property?.property_id}
          tranlations={contactTranslation}
        />

        <Rentals
          title={pageTranslations.rentals.title[router.locale]}
          cardTranslation={generalTranslation}
          target="real-estate"
          buttonLabel={pageTranslations.rentals.button[router.locale]}
          buttonLink="/real-estate/search"
          background="gray"
          properties={recommendedProperties}
        />
      </Page>
    )
  }
}

export async function getStaticPaths() {
  const data = await axios
    .get('real-estate/properties-name')
    .then((res) => res.data)
    .catch((error) => console.error(error))

  const paths =
    data &&
    data.map((post) => ({
      params: {
        ...post
      }
    }))

  return {
    paths,
    fallback: true
  }
}

export async function getStaticProps({ params, locale }) {
  const { _name } = params

  const data = await axios
    .get(`real-estate/show/${_name}`, { params: _name })
    .then((res) => res.data[locale])
    .catch((error) => {
      console.error(error)
      return error.response.status
    })

  const recommendedProperties = await axios
    .get('real-estate/similar', {
      params: {
        bedrooms: data?.bedrooms,
        propertyId: data?.resource_id || data?.property_id,
        neighbourhood: data?.neighbourhood,
        price: data?.property_price || data?.price?.usd
      }
    })
    .then((res) => res.data[locale])

  let headerDataTranslation = cache.get('headerTranslations')
  if (!headerDataTranslation) {
    headerDataTranslation = await headerTranslation()
    cache.put('headerTranslations', headerDataTranslation)
  }

  let generalTranslation = cache.get('generalRealEstateTranslations')
  if (!generalTranslation) {
    generalTranslation = await generalEstateTranslations()
    cache.put('generalRealEstateTranslations')
  }

  // caching for footer translations
  let footerDataTranslation = cache.get('footerTranslations')
  if (!footerDataTranslation) {
    footerDataTranslation = await footerTranslation()
    cache.put('footerTranslations', footerDataTranslation)
  }

  let contactFormTranslationData = cache.get('contactFormTranslations')
  if (!contactFormTranslationData) {
    contactFormTranslationData = await contactTranslation()
  }
  const translatedPageData = await singlePropertyTranslations()

  return {
    props: {
      property: data,
      recommendedProperties: recommendedProperties || false,
      notFound: !data || data.length === 0,
      generalTranslation: generalTranslation,
      pageTranslations: translatedPageData,
      headerTranslation: headerDataTranslation,
      footerTranslation: footerDataTranslation,
      contactTranslation: contactFormTranslationData
    },
    revalidate: 300
  }
}

export default RealEstateProperty
